import { authUserMetadataSchema } from '@aikoa/common'
import { type Language, languages } from '@aikoa/i18n/react'
import { useTolgee } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, type PropsWithChildren } from 'react'
import { useSession } from '~/components/user/SessionProvider'
import { supabase } from '~/lib/supabase'
import { trpc } from '~/lib/trpc'

function isValidLanguage(language: string | undefined): language is Language {
  return language !== undefined && languages.includes(language as Language)
}

export function LanguageSyncProvider({ children }: PropsWithChildren) {
  const tolgee = useTolgee(['language'])
  const { session } = useSession()
  const hasInitializedFromUser = useRef(false)
  const hasDetectedFromRequest = useRef(false)
  const isUpdatingFromUser = useRef(false)

  const { data: detectedLanguage } = useQuery(trpc.users.detectLanguage.queryOptions())

  useEffect(() => {
    if (session?.user || hasDetectedFromRequest.current || detectedLanguage === undefined) {
      return
    }

    hasDetectedFromRequest.current = true

    if (detectedLanguage && isValidLanguage(detectedLanguage) && detectedLanguage !== tolgee.getLanguage()) {
      void tolgee.changeLanguage(detectedLanguage)
    }
  }, [session, tolgee, detectedLanguage])

  useEffect(() => {
    if (!session?.user || hasInitializedFromUser.current) {
      return
    }

    const userMetadata = authUserMetadataSchema.safeParse(session.user.user_metadata)
    const userLanguage = userMetadata.data?.language
    const currentLanguage = tolgee.getLanguage()

    if (isValidLanguage(userLanguage) && userLanguage !== currentLanguage) {
      isUpdatingFromUser.current = true
      void tolgee.changeLanguage(userLanguage).finally(() => {
        isUpdatingFromUser.current = false
      })
    }

    hasInitializedFromUser.current = true
  }, [session, tolgee])

  useEffect(() => {
    if (!session?.user) {
      return
    }

    const unsubscribe = tolgee.on('language', (event) => {
      if (isUpdatingFromUser.current) {
        return
      }

      const newLanguage = event.value

      if (!newLanguage) {
        return
      }

      const userMetadata = authUserMetadataSchema.safeParse(session.user.user_metadata)
      const userLanguage = userMetadata.data?.language

      if (newLanguage !== userLanguage) {
        void supabase.auth.updateUser({
          data: { language: newLanguage },
        })
      }
    })

    return () => {
      unsubscribe.unsubscribe()
    }
  }, [session, tolgee])

  return children
}
